import { ChevronDown, Apple } from "lucide-react";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import homeScreen from "@/assets/screenshots/home.png";
import { APP_STORE_URL } from "@/lib/constants";

const HeroSection = () => {
  return (
    <section className="relative min-h-[90vh] flex items-center overflow-hidden">
      {/* Background glow orbs */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-20 right-[15%] w-[500px] h-[500px] rounded-full bg-accent/10 blur-[120px] animate-glow-pulse" />
        <div className="absolute bottom-20 left-[10%] w-[400px] h-[400px] rounded-full bg-primary/8 blur-[100px] animate-glow-pulse" style={{ animationDelay: "1.5s" }} />
      </div>

      <div className="container relative py-16 md:py-24">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Text */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="order-2 lg:order-1"
          >
            <div className="inline-flex items-center gap-2 glass-card px-4 py-1.5 rounded-full text-sm font-medium text-muted-foreground mb-8">
              <span className="w-2 h-2 rounded-full bg-accent animate-pulse" />
              Nu på App Store · Android kommer senere
            </div>

            <h1 className="text-display-sm md:text-display text-foreground mb-6">
              Dit sabbatår
              <br />
              <span className="text-accent">starter her</span>
            </h1>

            <p className="text-lg md:text-xl text-muted-foreground max-w-lg leading-relaxed mb-10">
              MySabbat samler job, rejser, højskole og planlægning i én app — så du kan fokusere på det, der betyder noget.
            </p>

            <div className="flex flex-wrap gap-4 mb-12">
              <Button
                asChild
                size="xl"
                className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full gap-2 shadow-lg"
              >
                <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer">
                  Download på App Store
                  <Apple className="w-4 h-4" />
                </a>
              </Button>
              <Button
                asChild
                size="xl"
                variant="outline"
                className="rounded-full border-border/60 text-foreground hover:bg-secondary"
              >
                <a href="#features">Læs mere</a>
              </Button>
            </div>

            {/* Social proof */}
            <div className="flex items-center gap-6 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <span className="text-xl">🇩🇰</span>
                <span>Lavet i Danmark</span>
              </div>
              <div className="w-px h-4 bg-border" />
              <span>Gratis at downloade</span>
              <div className="w-px h-4 bg-border" />
              <span>iOS · Android kommer senere</span>
            </div>
          </motion.div>

          {/* Phone mockup — iPhone 17 Pro style */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="order-1 lg:order-2 flex justify-center"
          >
            <div className="relative">
              {/* iPhone 17 Pro frame */}
              <div className="relative w-[260px] md:w-[300px]">
                <div className="rounded-[3rem] bg-[#1a1a1a] p-[4px] shadow-2xl">
                  <div className="rounded-[2.75rem] bg-[#0a0a0a] p-[3px]">
                    <div className="relative rounded-[2.5rem] overflow-hidden bg-background" style={{ aspectRatio: '1260/2736' }}>
                      {/* Dynamic Island */}
                      <div className="absolute top-[10px] left-1/2 -translate-x-1/2 w-[80px] h-[24px] bg-black rounded-full z-10" />
                      {/* Screenshot */}
                      <img
                        src={homeScreen}
                        alt="MySabbat app — dit personlige overblik"
                        className="w-full h-full object-cover object-top"
                        fetchPriority="high"
                      />
                    </div>
                  </div>
                  {/* Side button (power) */}
                  <div className="absolute right-[-3px] top-[25%] w-[3px] h-[50px] bg-[#2a2a2a] rounded-r-sm" />
                  {/* Volume buttons */}
                  <div className="absolute left-[-3px] top-[20%] w-[3px] h-[28px] bg-[#2a2a2a] rounded-l-sm" />
                  <div className="absolute left-[-3px] top-[28%] w-[3px] h-[28px] bg-[#2a2a2a] rounded-l-sm" />
                </div>
              </div>

              {/* Floating cards */}
              <motion.div
                animate={{ y: [0, -12, 0] }}
                transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                className="absolute -left-12 top-24 glass-card p-3 rounded-2xl shadow-lg"
              >
                <div className="flex items-center gap-2">
                  <span className="text-xl">🌍</span>
                  <div>
                    <p className="text-xs font-semibold text-foreground">Klar til eventyr?</p>
                    <p className="text-[10px] text-muted-foreground">Start planlægningen</p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 8, repeat: Infinity, ease: "easeInOut", delay: 2 }}
                className="absolute -right-8 top-[55%] glass-card p-3 rounded-2xl shadow-lg"
              >
                <div className="flex items-center gap-2">
                  <span className="text-lg">✈️</span>
                  <span className="text-xs font-semibold text-foreground">2026</span>
                </div>
              </motion.div>

              {/* Glow behind phone */}
              <div className="absolute -inset-8 -z-10 bg-accent/10 rounded-full blur-[60px]" />
            </div>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          className="hidden lg:flex justify-center mt-12"
        >
          <a href="#features" className="flex flex-col items-center gap-2 text-muted-foreground/50 hover:text-muted-foreground transition-colors">
            <span className="text-xs tracking-wider uppercase">Scroll</span>
            <ChevronDown className="w-4 h-4 animate-bounce-slow" />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default HeroSection;
